import type { CSSProperties } from "react";
import { Label } from "../common/Controls.js";
import { PegCluster } from "./Safe.js";
import type { Peg } from "./engine.js";
import { PAYOUT_BY_ATTEMPT } from "./engine.js";

/**
 * Cipher Crack — keypad, status pill, payout ladder, how-to legend, guess
 * history row.
 *
 * Ported from the design handoff `Controls.jsx` and shrunk to fit a
 * 1280×720 viewport: keypad buttons 52×42 (was 56×48), payout multiplier
 * font 18 (was 22), guess-history mini-slots 26×34 (was 30×38).
 */

// ---- KeypadButton -------------------------------------------------------
// One key. variant: digit / clear / enter. state: idle / hover / pressed /
// disabled / active (active applies only to ENTER when 4 digits are filled).

export type KeyVariant = "digit" | "clear" | "enter";
export type KeyState = "idle" | "hover" | "pressed" | "disabled" | "active";

interface KeyStyles {
  bg: string;
  fg: string;
  border: string;
  boxShadow: string;
  transform?: string;
}

function stylesFor(variant: KeyVariant, state: KeyState): KeyStyles {
  if (variant === "enter") {
    if (state === "active")
      return {
        bg: "linear-gradient(180deg, var(--gold-bright) 0%, var(--gold) 50%, var(--gold-deep) 100%)",
        fg: "var(--gold-shadow)",
        border: "var(--gold-deep)",
        boxShadow:
          "0 0 18px rgba(241,203,107,0.5), inset 0 1px 0 rgba(255,232,168,0.3), inset 0 -1px 0 rgba(74,54,20,0.4)",
      };
    if (state === "disabled")
      return {
        bg: "linear-gradient(180deg, #1A1F2C 0%, #11151F 100%)",
        fg: "var(--fg-4)",
        border: "var(--line)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
      };
    if (state === "pressed")
      return {
        bg: "linear-gradient(180deg, var(--gold) 0%, var(--gold-deep) 100%)",
        fg: "var(--gold-shadow)",
        border: "var(--gold-deep)",
        boxShadow:
          "0 0 12px rgba(241,203,107,0.4), inset 0 1px 2px rgba(0,0,0,0.4)",
        transform: "translateY(1px)",
      };
    return {
      bg: "linear-gradient(180deg, #1F1A0E 0%, #15110A 100%)",
      fg: "var(--gold-bright)",
      border: "rgba(196,154,70,0.42)",
      boxShadow: "inset 0 1px 0 rgba(255,232,168,0.05)",
    };
  }
  if (variant === "clear") {
    if (state === "pressed")
      return {
        bg: "linear-gradient(180deg, #2A1414 0%, #1A0A0A 100%)",
        fg: "var(--red-bright)",
        border: "rgba(224,69,69,0.55)",
        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.5)",
        transform: "translateY(1px)",
      };
    if (state === "hover")
      return {
        bg: "linear-gradient(180deg, #1F1416 0%, #150A0C 100%)",
        fg: "var(--red-bright)",
        border: "rgba(224,69,69,0.55)",
        boxShadow:
          "0 0 10px rgba(224,69,69,0.18), inset 0 1px 0 rgba(255,255,255,0.02)",
      };
    if (state === "disabled")
      return {
        bg: "linear-gradient(180deg, #11151F 0%, #0A0D14 100%)",
        fg: "var(--fg-4)",
        border: "var(--line)",
        boxShadow: "none",
      };
    return {
      bg: "linear-gradient(180deg, #181014 0%, #100808 100%)",
      fg: "rgba(255,106,106,0.78)",
      border: "rgba(224,69,69,0.28)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
    };
  }
  // digit
  if (state === "pressed")
    return {
      bg: "linear-gradient(180deg, #11151F 0%, #07090F 100%)",
      fg: "var(--gold-bright)",
      border: "rgba(241,203,107,0.55)",
      boxShadow:
        "inset 0 1px 3px rgba(0,0,0,0.6), 0 0 8px rgba(241,203,107,0.35)",
      transform: "translateY(1px)",
    };
  if (state === "hover")
    return {
      bg: "linear-gradient(180deg, #181D29 0%, #11151F 100%)",
      fg: "var(--gold-bright)",
      border: "rgba(196,154,70,0.55)",
      boxShadow:
        "0 0 10px rgba(196,154,70,0.2), inset 0 1px 0 rgba(255,255,255,0.04)",
    };
  if (state === "disabled")
    return {
      bg: "linear-gradient(180deg, #0E1118 0%, #080B11 100%)",
      fg: "var(--fg-4)",
      border: "rgba(196,154,70,0.08)",
      boxShadow: "none",
    };
  return {
    bg: "linear-gradient(180deg, #141925 0%, #0B0E16 100%)",
    fg: "var(--fg-1)",
    border: "rgba(196,154,70,0.22)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.025), inset 0 -1px 0 rgba(0,0,0,0.4)",
  };
}

export function KeypadButton({
  label,
  variant = "digit",
  state = "idle",
  width = 52,
  height = 42,
  onClick,
  ariaLabel,
}: {
  label: string;
  variant?: KeyVariant;
  state?: KeyState;
  width?: number;
  height?: number;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const s = stylesFor(variant, state);
  const handle = state === "disabled" ? undefined : onClick;
  return (
    <button
      type="button"
      onClick={handle}
      aria-label={ariaLabel ?? label}
      disabled={state === "disabled"}
      style={{
        width,
        height,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 3,
        boxShadow: s.boxShadow,
        transform: s.transform ?? "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: state === "disabled" ? "not-allowed" : "pointer",
        transition: "transform 80ms ease, border-color 120ms ease, box-shadow 120ms ease",
        padding: 0,
        appearance: "none",
        userSelect: "none",
        color: s.fg,
        fontFamily: variant === "digit" ? "var(--font-mono)" : "var(--font-display)",
        fontSize: variant === "digit" ? Math.round(height * 0.42) : Math.round(height * 0.32),
        fontWeight: variant === "digit" ? 600 : 500,
        letterSpacing: variant === "digit" ? 0 : 2,
        lineHeight: 1,
      }}
    >
      {label}
    </button>
  );
}

// ---- Keypad -------------------------------------------------------------
// 12-button grid: 1-2-3 / 4-5-6 / 7-8-9 / CLR-0-ENTER

export function Keypad({
  onDigit,
  onClear,
  onSubmit,
  enterActive = false,
  disabled = false,
  buttonW = 52,
  buttonH = 42,
  gap = 4,
}: {
  onDigit?: (d: number) => void;
  onClear?: () => void;
  onSubmit?: () => void;
  enterActive?: boolean;
  disabled?: boolean;
  buttonW?: number;
  buttonH?: number;
  gap?: number;
}) {
  const stateOf = (kind: KeyVariant): KeyState => {
    if (disabled) return "disabled";
    if (kind === "enter" && enterActive) return "active";
    return "idle";
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap, alignItems: "center" }}>
      {[0, 1, 2].map((row) => (
        <div key={row} style={{ display: "flex", gap }}>
          {[1, 2, 3].map((col) => {
            const d = row * 3 + col;
            return (
              <KeypadButton
                key={d}
                label={String(d)}
                variant="digit"
                state={stateOf("digit")}
                width={buttonW}
                height={buttonH}
                onClick={() => onDigit?.(d)}
              />
            );
          })}
        </div>
      ))}
      <div style={{ display: "flex", gap }}>
        <KeypadButton
          label="CLR"
          variant="clear"
          state={stateOf("clear")}
          width={buttonW}
          height={buttonH}
          onClick={onClear}
        />
        <KeypadButton
          label="0"
          variant="digit"
          state={stateOf("digit")}
          width={buttonW}
          height={buttonH}
          onClick={() => onDigit?.(0)}
        />
        <KeypadButton
          label="ENTER"
          variant="enter"
          state={stateOf("enter")}
          width={buttonW}
          height={buttonH}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}

// ---- StatusPill ---------------------------------------------------------

export type StatusKind = "idle" | "awaiting" | "live" | "cracked" | "sealed" | "cashed_out";

const PILL_CFG: Record<StatusKind, { label: (a: number, t: number) => string; color: string; dot: string; border: string }> = {
  idle: {
    label: () => "AWAITING DEPLOY",
    color: "var(--fg-2)",
    dot: "var(--fg-3)",
    border: "rgba(110,117,137,0.35)",
  },
  awaiting: {
    label: () => "AWAITING GUESS",
    color: "var(--fg-2)",
    dot: "var(--fg-3)",
    border: "rgba(110,117,137,0.35)",
  },
  live: {
    label: (a, t) => `ATTEMPT 0${a} OF 0${t}`,
    color: "var(--cyan-bright)",
    dot: "var(--cyan)",
    border: "rgba(107,198,224,0.35)",
  },
  cracked: {
    label: () => "CODE CRACKED",
    color: "var(--green-bright)",
    dot: "var(--green)",
    border: "rgba(91,208,138,0.45)",
  },
  sealed: {
    label: () => "VAULT SEALED",
    color: "var(--red-bright)",
    dot: "var(--red)",
    border: "rgba(224,69,69,0.5)",
  },
  cashed_out: {
    label: () => "ROUND CASHED",
    color: "var(--cyan-bright)",
    dot: "var(--cyan)",
    border: "rgba(107,198,224,0.55)",
  },
};

export function StatusPill({
  kind = "idle",
  attempt = 1,
  total = 4,
}: {
  kind?: StatusKind;
  attempt?: number;
  total?: number;
}) {
  const cfg = PILL_CFG[kind];
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 10px",
        background: "rgba(11,14,22,0.7)",
        border: `1px solid ${cfg.border}`,
        borderRadius: 2,
        fontFamily: "var(--font-ui)",
        fontSize: 10,
        fontWeight: 800,
        color: cfg.color,
        letterSpacing: 2.5,
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.color,
          boxShadow: `0 0 8px ${cfg.dot}`,
        }}
      />
      {cfg.label(attempt, total)}
    </div>
  );
}

// ---- PayoutLadder -------------------------------------------------------
// 5 rungs, current attempt highlighted.

export type LadderMode = "idle" | "live" | "cracked" | "sealed";

const RUNG_LABELS = ["FIRST CRACK", "QUICK", "STEADY", "LAST CHANCE"] as const;

export function PayoutLadder({
  currentAttempt = 1,
  mode = "idle",
}: {
  currentAttempt?: number;
  mode?: LadderMode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {PAYOUT_BY_ATTEMPT.map((mult, i) => {
        const att = i + 1;
        const isPast = mode === "live" && att < currentAttempt;
        const isCurrent = mode === "live" && att === currentAttempt;
        const isFuture = mode === "live" && att > currentAttempt;
        const isCracked = mode === "cracked" && att === currentAttempt;
        const isSealed = mode === "sealed";
        const isIdle = mode === "idle";
        // Visual fill scales with mult magnitude (20× full bar, 1.3× tiny).
        // 4 rungs now (was 5) — fill values tuned to keep the visual hierarchy
        // readable across the shorter ladder.
        const ladderFill = att === 1 ? 1 : att === 2 ? 0.55 : att === 3 ? 0.30 : 0.14;

        let fill = 0;
        let textCol = "var(--fg-2)";
        let multCol = "var(--gold-bright)";
        let edgeCol: string = "var(--line)";
        let bgCol: string = "rgba(11,14,22,0.5)";
        let glow = "none";
        let opacity = 1;

        if (isIdle) {
          fill = ladderFill;
          edgeCol = "rgba(196,154,70,0.32)";
        } else if (isPast) {
          fill = ladderFill * 0.18;
          textCol = "var(--fg-4)";
          multCol = "var(--fg-4)";
          edgeCol = "var(--line-bone)";
          opacity = 0.55;
        } else if (isCurrent) {
          fill = ladderFill;
          textCol = "var(--fg-1)";
          multCol = "var(--gold-bright)";
          edgeCol = "var(--gold-bright)";
          bgCol = "rgba(241,203,107,0.06)";
          glow = "0 0 14px rgba(241,203,107,0.18), inset 0 0 8px rgba(241,203,107,0.06)";
        } else if (isFuture) {
          fill = ladderFill * 0.6;
          textCol = "var(--fg-3)";
          multCol = "var(--gold)";
          edgeCol = "rgba(196,154,70,0.18)";
          opacity = 0.85;
        } else if (isCracked) {
          fill = ladderFill;
          textCol = "var(--gold-shadow)";
          multCol = "var(--gold-shadow)";
          edgeCol = "var(--gold-bright)";
          bgCol = "linear-gradient(180deg, var(--gold-bright), var(--gold))";
          glow = "0 0 18px rgba(241,203,107,0.55)";
        } else if (isSealed) {
          fill = ladderFill * 0.12;
          textCol = "var(--fg-4)";
          multCol = "var(--fg-4)";
          edgeCol = "var(--line-bone)";
          opacity = 0.5;
        }

        const rungStyle: CSSProperties = {
          position: "relative",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px",
          background: bgCol,
          border: `1px solid ${edgeCol}`,
          borderRadius: 2,
          boxShadow: glow,
          opacity,
          overflow: "hidden",
        };

        return (
          <div key={att} style={rungStyle}>
            {/* Bar fill */}
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${fill * 100}%`,
                background: isCracked
                  ? "transparent"
                  : "linear-gradient(90deg, rgba(196,154,70,0.18) 0%, rgba(196,154,70,0.04) 100%)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 600,
                color: textCol,
                letterSpacing: 1,
                minWidth: 20,
              }}
            >
              0{att}
            </div>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                fontFamily: "var(--font-ui)",
                fontSize: 8,
                fontWeight: 700,
                color: textCol,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              {RUNG_LABELS[i]}
            </div>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                fontFamily: "var(--font-display)",
                fontSize: 18,
                color: multCol,
                letterSpacing: -0.5,
                lineHeight: 1,
                textShadow: isCurrent ? "0 0 8px var(--gold)" : "none",
              }}
            >
              {mult >= 10 ? mult.toFixed(0) : mult.toFixed(1)}
              <span style={{ fontSize: 11, marginLeft: 1 }}>×</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- HowToCrack legend --------------------------------------------------
// Compact one-row legend — used to be a stacked 3-row explainer that ate
// ~280px of left-rail height. The dense form is fine in the standalone
// /play/cipher-crack route; inside /casino's iframe it pushed LAUNCH ROUND
// below the fold.

const LEGEND_ITEMS: ReadonlyArray<{ peg: Peg; text: string }> = [
  { peg: "gold", text: "right slot" },
  { peg: "silver", text: "wrong slot" },
  { peg: "miss", text: "miss" },
];

export function HowToCrack() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "6px 8px",
        background: "rgba(17,21,31,0.5)",
        border: "1px solid var(--line-bone)",
        borderRadius: 3,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Label>How to crack</Label>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--fg-4)",
            letterSpacing: 0.5,
          }}
          title="Peg 1 reads slot 1, peg 2 reads slot 2, etc."
        >
          slot-mapped
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          fontFamily: "var(--font-ui)",
          fontSize: 10,
          color: "var(--fg-2)",
        }}
      >
        {LEGEND_ITEMS.map((it) => (
          <span key={it.peg} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <PegCluster pegs={[it.peg]} size={10} sort={false} />
            <span>{it.text}</span>
          </span>
        ))}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          color: "var(--fg-3)",
          lineHeight: 1.3,
        }}
      >
        Match all 4 digits.
        <span style={{ color: "var(--gold-bright)" }}> Earlier crack = bigger payout.</span>
      </div>
    </div>
  );
}

// ---- GuessHistoryRow ----------------------------------------------------

export function GuessHistoryRow({
  attempt,
  digits,
  pegs,
  dim = true,
}: {
  attempt: number;
  digits: ReadonlyArray<number>;
  pegs: ReadonlyArray<Peg>;
  dim?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "26px 1fr auto",
        gap: 10,
        alignItems: "center",
        padding: "6px 10px",
        background: "rgba(17,21,31,0.4)",
        border: "1px solid var(--line-bone)",
        borderRadius: 3,
        opacity: dim ? 0.85 : 1,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          color: "var(--fg-3)",
          letterSpacing: 1,
        }}
      >
        0{attempt}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {digits.map((d, i) => (
          <div
            key={i}
            style={{
              width: 26,
              height: 34,
              background: "linear-gradient(180deg, #141925, #0B0E16)",
              border: "1px solid rgba(196,154,70,0.18)",
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-display)",
              fontSize: 18,
              color: "var(--gold-bright)",
              letterSpacing: -1,
              textShadow: "0 0 5px var(--gold-deep)",
            }}
          >
            {d}
          </div>
        ))}
      </div>
      <PegCluster pegs={pegs} size={11} layout="row" />
    </div>
  );
}
